import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Grid2 } from "@mui/material";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import AdvertisementTypes from "components/common/AdvertisementTypes";
import { Paths, matchWith } from "components/common/navigation";
import { currentUserIdSelector, currentUserPrivilegesSelector } from "components/login/logic/actions";
import {
    FullPasswordFields,
    PasswordChangeForm,
    passwordMatch,
    passwordRuleSchema,
    usePasswordTypeChange,
} from "components/settings/user/form/passwordForm";
import { ACTION_STATES } from "core/actions";
import { assertString } from "core/validation";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import { ROLES_OPTIONS } from "services/userService";
import * as Yup from "yup";
import UserCard from "../list/userCard";
import { actions } from "./logic/actions";
import useStyles from "./styles";

const nameRules = Yup.string()
    .nullable()
    .required("Required")
    .matches(/^[-aA-zZ0-9_\s]+$/, "Special characters are not supported")
    .min(2, "Must contain at least 2 characters")
    .max(50, "Should contain 50 characters or less")
    .trim();
const validateUser = Yup.object().shape({
    username: Yup.string()
        .required("Required")
        .min(2, "Must contain at least 2 characters")
        .max(50, "Should contain 50 characters or less")
        .trim(),
    avatarText: Yup.string()
        .nullable()
        .required("Avatar text is Required")
        .matches(/^[aA-zZ0-9\s]+$/, "Please enter valid avatar text")
        .min(1, "Only two characters")
        .max(2, "Only two characters"),
    firstName: nameRules,
    lastName: nameRules,
    email: Yup.string().nullable().required("Required").email("Invalid address format"),
    role: Yup.object().nullable().required("Required"),
});

export const newDefaultUser = {
    active: true,
    role: ROLES_OPTIONS[0],
    avatarText: null,
    firstName: null,
    lastName: null,
    email: null,
    newPassword: null,
    newPasswordConfirmation: null,
    ssoEnabled: true,
};
export const validationSchemaPasswordChange = Yup.object().shape({
    oldPassword: Yup.string().nullable(),

    newPassword: Yup.string()
        .nullable()
        .when("oldPassword", {
            is: (val) => !!val,
            then: () => Yup.string().required("Required").concat(passwordRuleSchema),
            otherwise: () => Yup.string().nullable().notRequired(),
        }),

    newPasswordConfirmation: Yup.string().nullable().concat(passwordMatch),
});

const passwordSchemaRequired = Yup.object().shape({
    newPassword: Yup.string().nullable().required("Required").concat(passwordRuleSchema),
    newPasswordConfirmation: Yup.string().nullable().required("Required").concat(passwordMatch),
});

const passwordSchemaNotRequired = Yup.object().shape({
    newPassword: Yup.string().nullable()
        .when({
            is: (val) => !!val,
            then: () => Yup.string().concat(passwordRuleSchema),
            otherwise: () => Yup.string().nullable().notRequired(),
        }),
    newPasswordConfirmation: Yup.string().nullable().concat(passwordMatch),
});
function UserForm({ onCancel, isNew }) {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.userForm.userData.form);
    const savingStatus = useSelector((state) => state.userForm.savingStatus);
    const [saving, setSaving] = useState(false);
    const currentPrivileges = useSelector(currentUserPrivilegesSelector);
    const currentUserId = useSelector(currentUserIdSelector);
    const [type, showPassword, toggleShowPassword] = usePasswordTypeChange();
    const [ssoEnabled, setSsoEnabled] = useState(newDefaultUser.ssoEnabled);
    const { pathname } = useLocation();
    const canShowPassword = currentPrivileges.isAdmin;

    const navigate = useNavigate();
    useEffect(() => {
        if ((saving && savingStatus === ACTION_STATES.SUCCESS) || savingStatus === ACTION_STATES.FAILED) {
            setSaving(false);
            const isProfile = matchWith(pathname, Paths.PROFILE);
            if (savingStatus === ACTION_STATES.SUCCESS) {
                if (currentPrivileges.isAdmin && !isProfile) {
                    navigate(Paths.ADMIN_USER);
                } else {
                    navigate(-1);
                }
            }
        }
    }, [currentPrivileges.isAdmin, navigate, pathname, saving, savingStatus, setSaving]);

    const classes = useStyles();
    const id = user ? user.id : null;

    const editingProfile = id === currentUserId;
    const passwordSchema = useMemo(() => {
        if (ssoEnabled) {
            return null;
        }
        if (editingProfile) {
            return validationSchemaPasswordChange;
        }
        if (isNew && canShowPassword) {
            return passwordSchemaRequired;
        }
        if (canShowPassword) {
            return passwordSchemaNotRequired;
        }
        return null;
    }, [ssoEnabled, editingProfile, isNew, canShowPassword]);

    const formik = useFormik({
        initialValues: isNew ? newDefaultUser : Object.assign({}, newDefaultUser, user),
        onSubmit: handleSubmit,
        validationSchema: passwordSchema ? validateUser.concat(passwordSchema) : validateUser,
    });

    const defaultAvatarText = useMemo(() => {
        if (!formik.touched.avatarText && isNew) {
            let result = "";
            const firstname = formik.values.firstName;
            const lastname = formik.values.lastName;
            if (assertString(firstname)) {
                result += firstname.toUpperCase().substring(0, 1);
            }
            if (assertString(lastname)) {
                result += lastname.toUpperCase().substring(0, 1);
            }
            return assertString(result) ? result : null;
        } else {
            return formik.values.avatarText;
        }
    }, [formik.values.firstName, formik.values.lastName, isNew, formik.values.avatarText, formik.touched.avatarText]);

    useEffect(() => {
        if (!formik.touched.avatarText && isNew) {
            formik.setFieldValue("avatarText", defaultAvatarText);
        }
    }, [defaultAvatarText]);

    useEffect(() => {
        if (!isNew && user) {
            setSsoEnabled(user.ssoEnabled);
            Object.assign({}, newDefaultUser, user);
        }
    }, [isNew, user]);

    function handleSubmit() {
        if (formik.isValid) {
            const newValues = {...formik.values, ssoEnabled: ssoEnabled};
            setSaving(true);
            dispatch(actions.SAVE_USER.send(newValues));
        }
    }

    function handleClickSubmit() {
        formik.submitForm();
    }
    function onChangeColor(e) {
        formik.setFieldValue("avatarColor", e.target.value);
    }

    function handleOnChangeAdvTypes(values) {
        formik.setFieldValue("advertisementTypes", values);
    }

    function handleChangeRole(value) {
        formik.setFieldValue("role", value);
    }

    const cardUser = {
        avatarText: formik.values.avatarText,
        avatarColor: formik.values.avatarColor,
    };
    const canEditSettings = currentPrivileges.isAdmin && (isNew || !editingProfile);

    function handleAvatarTextFocus() {
        formik.setFieldTouched("avatarText", true);
    }

    return (
        (<form autoComplete={"off"}>
            <Container maxWidth={"lg"}>
                <Grid2 container spacing={5}>
                    <Grid2 size={12}>
                        <Box className={classes.formButtonsContainer}>
                            <Button variant={"outlined"} onClick={onCancel}>
                                Cancel
                            </Button>
                            <Button
                                variant={"contained"}
                                color={"primary"}
                                disabled={saving}
                                onClick={handleClickSubmit}
                            >
                                Save
                            </Button>
                        </Box>
                    </Grid2>
                    <Grid2 size={12}>
                        <Divider />
                    </Grid2>
                    <Grid2
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 4
                        }}>
                        <Box style={{ marginTop: 10 }}>
                            <Grid2 container spacing={2}>
                                <Grid2 size={12}>
                                    <UserCard user={cardUser}>
                                        <Box>
                                            <Grid2 container spacing={2}>
                                                <Grid2 style={{ textAlign: "center" }} size={6}>
                                                    <Typography variant={"subtitle2"} className={"headerForm"}>
                                                        Avatar text
                                                    </Typography>
                                                    <TextField
                                                        className="form-control"
                                                        margin="normal"
                                                        name={"avatarText"}
                                                        style={{ maxWidth: 45 }}
                                                        onChange={formik.handleChange}
                                                        value={formik.values.avatarText}
                                                        variant="outlined"
                                                        onFocus={handleAvatarTextFocus}
                                                        onBlur={formik.handleBlur}
                                                        error={
                                                            formik.touched.avatarText &&
                                                            Boolean(formik.errors.avatarText)
                                                        }
                                                    />
                                                </Grid2>

                                                <Grid2 style={{ textAlign: "center" }} size={6}>
                                                    <Typography variant={"subtitle2"} className={"headerForm"}>
                                                        Avatar Color
                                                    </Typography>
                                                    <input
                                                        type="color"
                                                        name="avatarColor"
                                                        onChange={onChangeColor}
                                                        className="form-control"
                                                        style={{
                                                            marginTop: 16,
                                                            height: 37,
                                                        }}
                                                        value={formik.values.avatarColor}
                                                    />
                                                </Grid2>
                                                {formik.touched.avatarText && formik.errors.avatarText && (
                                                    <Grid2 style={{ textAlign: "center" }} size={12}>
                                                        <Typography color={"error"}>
                                                            {formik.errors.avatarText}
                                                        </Typography>
                                                    </Grid2>
                                                )}
                                            </Grid2>
                                        </Box>
                                    </UserCard>
                                </Grid2>
                            </Grid2>
                        </Box>
                    </Grid2>
                    <Grid2
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 6
                        }}>
                        <Box>
                            <Grid2 container spacing={3}>
                                <Grid2 size={12}>
                                    <Typography variant={"subtitle2"} className={"headerForm"}>
                                        Username
                                    </Typography>
                                    <TextField
                                        className="form-control"
                                        margin="normal"
                                        name={"username"}
                                        onChange={formik.handleChange}
                                        value={formik.values.username}
                                        variant="outlined"
                                        autoComplete={"new-username"}
                                        onBlur={formik.handleBlur}
                                        helperText={formik.touched.username ? formik.errors.username : ""}
                                        error={formik.touched.username && Boolean(formik.errors.username)}
                                        slotProps={{
                                            htmlInput: {
                                                autoComplete: "new-username",
                                            }
                                        }}
                                    />
                                </Grid2>
                                <Grid2 size={12}>
                                    <Typography variant={"subtitle2"} className={"headerForm"}>
                                        First Name
                                    </Typography>
                                    <TextField
                                        className="form-control"
                                        margin="normal"
                                        name={"firstName"}
                                        autoComplete={"new-firstname"}
                                        onChange={formik.handleChange}
                                        value={formik.values.firstName}
                                        variant="outlined"
                                        onBlur={formik.handleBlur}
                                        helperText={formik.touched.firstName ? formik.errors.firstName : ""}
                                        error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                                        slotProps={{
                                            htmlInput: {
                                                autoComplete: "new-firstname",
                                            }
                                        }}
                                    />
                                </Grid2>
                                <Grid2 size={12}>
                                    <Typography variant={"subtitle2"} className={"headerForm"}>
                                        Last Name
                                    </Typography>
                                    <TextField
                                        className="form-control"
                                        margin="normal"
                                        name={"lastName"}
                                        onChange={formik.handleChange}
                                        value={formik.values.lastName}
                                        variant="outlined"
                                        autoComplete={"new-lastname"}
                                        onBlur={formik.handleBlur}
                                        helperText={formik.touched.lastName ? formik.errors.lastName : ""}
                                        error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                                        slotProps={{
                                            htmlInput: {
                                                autoComplete: "new-lastname",
                                            }
                                        }}
                                    />
                                </Grid2>
                                <Grid2 size={12}>
                                    <Typography variant={"subtitle2"} className={"headerForm"}>
                                        Email
                                    </Typography>
                                    <TextField
                                        className="form-control"
                                        margin="normal"
                                        name={"email"}
                                        value={formik.values.email}
                                        variant="outlined"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        helperText={formik.touched.email ? formik.errors.email : ""}
                                        error={formik.touched.email && Boolean(formik.errors.email)}
                                    />
                                </Grid2>
                                <Grid2 size={12}>
                                    <Typography
                                        variant={"subtitle2"}
                                        className={"headerForm"}
                                        style={{ marginBottom: 10 }}
                                    >
                                        Default Advertisement Types
                                    </Typography>
                                    <AdvertisementTypes
                                        onChange={handleOnChangeAdvTypes}
                                        className="form-control"
                                        placeholder={"Select types"}
                                        value={formik.values.advertisementTypes}
                                    />
                                </Grid2>

                                {(canShowPassword || editingProfile || canEditSettings) && (
                                    <>
                                        <Grid2 size={12}>
                                            <Typography variant={"h6"}>Security</Typography>
                                            <Divider />
                                        </Grid2>
                                        {canEditSettings && (
                                            <>
                                                <Grid2 size={12}>
                                                    <Typography
                                                        variant={"subtitle2"}
                                                        style={{ marginBottom: 10 }}
                                                        className={"headerForm"}
                                                    >
                                                        Role
                                                    </Typography>
                                                    <Select
                                                        placeholder="Select Roles"
                                                        value={formik.values.role}
                                                        name={"role"}
                                                        onChange={handleChangeRole}
                                                        options={ROLES_OPTIONS}
                                                    />
                                                </Grid2>
                                                <Grid2 size={12}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={formik.values.active}
                                                                onChange={formik.handleChange}
                                                                name="active"
                                                                color="primary"
                                                            />
                                                        }
                                                        label="Active user"
                                                    />
                                                </Grid2>
                                                <Grid2 size={12}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={ssoEnabled}
                                                                onChange={(e) => setSsoEnabled(e.target.checked)}
                                                                name="ssoEnabled"
                                                                color="primary"
                                                            />
                                                        }
                                                        label="Use Single SignOn"
                                                    />
                                                </Grid2>
                                            </>
                                        )}
                                        {!ssoEnabled ? (
                                            editingProfile ? (
                                                <FullPasswordFields type={type} formik={formik} />
                                            ) : (
                                                <PasswordChangeForm isNew={isNew} type={type} formik={formik} />
                                            )
                                        ) : (
                                            <></>
                                        )}

                                        {!ssoEnabled ? (
                                            <Grid2 size={12}>
                                                <Button
                                                    variant={"text"}
                                                    type={"button"}
                                                    size={"small"}
                                                    color={"secondary"}
                                                    onClick={toggleShowPassword}
                                                >
                                                    {showPassword ? "Hide passwords" : "Show passwords"}
                                                </Button>
                                            </Grid2>
                                        ) : (
                                            <></>
                                        )}
                                    </>
                                )}
                            </Grid2>
                        </Box>
                    </Grid2>
                </Grid2>
            </Container>
        </form>)
    );
}
UserForm.propTypes = {
    onCancel: PropTypes.func,
    isNew: PropTypes.bool,
};
export default UserForm;
